<script setup>
import {
  onBeforeMount,
  onBeforeUnmount,
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from "vue";
import { CUSTOM_EVENTS } from "../../../api/events.js";
import AlertItem from "./AlertItem.vue";

const props = defineProps({
  settings: {
    type: Object,
    default: () => ({}),
  },
  user: {
    type: Object,
    default: null,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  cache: {
    type: Object,
    default: () => ({ events: [] }),
  },
});

const emit = defineEmits(["alert-ended"]);

const alertQueue = ref([]);
const events = ref([]); // alert event configurations
// Monotonic counter so every queued alert gets a unique :key → AlertItem always
// remounts and its watchdog re-arms (even if a request id repeats, e.g. tests).
let alertSeq = 0;

const currentAlert = computed(() => {
  return alertQueue.value[0] || null;
});

/**
 * Convert template placeholders to display text
 */
function convertTemplateToText(template, data) {
  let _template = template || "";
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return template;
  }

  keys.forEach((key) => {
    _template = _template.replaceAll(
      "#" + key,
      `<span class="alert-box-highlight">${data[key]}</span>`,
    );
  });

  return _template;
}

/**
 * Map a request event to an alert format
 */
function mapRequestToAlert(alert, eventConfig) {
  const request = alert.request || {};
  const product = request.product || {};
  
  // Note: the viewer's free-text input is intentionally NOT exposed here (no
  // `message` key) — we never render the viewer's message in an alert.
  const data = {
    user: alert.data?.user_name || request.userName || request.userId || "Viewer",
    request: product.name || "Request",
    value: request.value || 0,
  };

  const settings = eventConfig?.settings || props.settings || {};
  
  // Get sound and media from eventConfig first, then fall back to settings
  const sound = eventConfig?.sound || settings.sound || null;
  const media = eventConfig?.media || settings.media || null;
  
  return {
    id: `request-${request.id || "x"}-${alertSeq++}`,
    type: "request",
    data,
    text: convertTemplateToText(settings.textTemplate || "#user requested #request", data),
    sound: props.muted ? undefined : sound,
    media: media,
    settings: {
      text: settings.text || {
        fontFamily: "Inter",
        fontSize: "md",
        color: "#ffffff",
        position: "bottom",
        fontStyle: [],
      },
      textTemplate: settings.textTemplate || "#user requested #request",
      textHighlight: settings.textHighlight || {
        color: "#fbbf24",
        animation: "pulse",
      },
      // Respect "no outline" (null) — don't inject a default, so the widget
      // matches the in-config preview, which renders an outline only when set.
      textOutline: settings.textOutline || null,
      animation: settings.animation || {
        in: "fadeIn",
        out: "fadeOut",
      },
      tts: settings.tts || null,
    },
  };
}

/**
 * Handle incoming request alert events
 */
function onNewEvent(e) {
  const alert = e.detail;

  // Find matching event configuration or use defaults
  let eventConfig = null;
  
  if (alert.isTest && alert.alertSettings) {
    // alertSettings IS the settings object, but mapRequestToAlert (like the real
    // path) expects an event config that nests it under `.settings`. Wrap it so a
    // test renders the text/animation being previewed, not the saved scene's.
    eventConfig = { settings: alert.alertSettings };
  } else if (events.value.length > 0) {
    // Find matching config from cache
    eventConfig = events.value.find(ev => ev.type === "request") || events.value[0];
  }

  const mappedAlert = mapRequestToAlert(alert, eventConfig);
  alertQueue.value.push(mappedAlert);
}

function onAlertEnded() {
  if (!currentAlert.value) {
    console.warn("[AlertBoxWidget] No current alert to end");
    return;
  }
  emit("alert-ended", currentAlert.value);
  alertQueue.value.shift();
}

// Watch for cache updates
watch(
  () => props.cache.events,
  (newEvents) => {
    events.value = newEvents || [];
  },
);

// Container sizing for responsive fonts
const alertContainer = ref(null);
const containerWidth = ref(0);
const containerHeight = ref(0);
const containerResizeObserver = new ResizeObserver((entries) => {
  const entry = entries[0];
  const { width, height } = entry.contentRect;
  containerWidth.value = width;
  containerHeight.value = height;
});

onMounted(() => {
  nextTick(() => {
    if (alertContainer.value) {
      containerResizeObserver.observe(alertContainer.value);
      containerWidth.value = alertContainer.value.clientWidth;
      containerHeight.value = alertContainer.value.clientHeight;
    }
  });
});

const fontScale = computed(() => {
  const size = Math.min(containerWidth.value, containerHeight.value);
  return size / 400;
});

onBeforeMount(() => {
  events.value = props.cache.events || [];
  // Listen for request alert events
  window.addEventListener(CUSTOM_EVENTS.REQUEST_ALERT_EVENT, onNewEvent);
});

onBeforeUnmount(() => {
  window.removeEventListener(CUSTOM_EVENTS.REQUEST_ALERT_EVENT, onNewEvent);
  containerResizeObserver.disconnect();
});
</script>

<template>
  <div
    ref="alertContainer"
    class="alert-box-wrapper"
  >
    <AlertItem
      v-if="currentAlert"
      :current-alert="currentAlert"
      :key="currentAlert.id"
      @ended="onAlertEnded"
      @error="onAlertEnded"
      class="alert-item"
      :font-scale="fontScale"
    />
  </div>
</template>

<style scoped>
.alert-box-wrapper {
  max-width: 100%;
  max-height: 100%;
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
}

.alert-item {
  position: relative;
  height: 100%;
  width: 100%;
}
</style>
